/**
 * Building, parsing and describing an RRULE from the recurrence form (SPEC.md §4.6).
 *
 * The plain-English preview is generated directly from the form's own structured fields
 * rather than by parsing the built string back — the two can never disagree, and it reads
 * the exact way §4.6.2 asks for ("Every other Saturday, forever"), which `rrule.js`'s own
 * `toText()` does not quite manage for the `BYSETPOS` cases ("third Tuesday", "last Sunday
 * of the month") this form exists to cover.
 *
 * `rrule.js` itself is still load-bearing here: `buildRRuleString` and `parseRecurrence`
 * round-trip through a real `RRule` rather than hand-rolling string assembly or a regex
 * parser, and `previewDates` expands the rule locally for the form's own "next: …" line —
 * the PWA-side half of "do not hand-roll expansion" (§4.6), alongside `dateutil.rrule`
 * server-side.
 */

import { RRule, Weekday } from "rrule";

export type Freq = "DAILY" | "WEEKLY" | "MONTHLY" | "YEARLY";

/**
 * Today, in the browser's own local timezone — `YYYY-MM-DD`.
 *
 * Deliberately not `new Date().toISOString().slice(0, 10)`: `toISOString()` is UTC, and for
 * anyone west of it, the hours after local midday through midnight already report tomorrow.
 * That turned "add a routine with no due date" into "add a routine that starts tomorrow"
 * for exactly the reader in the timezone this bug is about — the same class of mistake
 * §5.3 exists to rule out server-side (never hardcode a zone; here, never assume UTC is
 * the reader's local day). `Date`'s own local getters already reflect the browser's zone,
 * which is what `X-Timezone` sends the server anyway.
 */
export function todayLocalDate(): string {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export type RecurrenceFormValues = {
  enabled: boolean;
  freq: Freq;
  interval: string;
  /** WEEKLY only — "MO".."SU". */
  byweekday: string[];
  /** MONTHLY only. */
  monthMode: "day" | "weekday";
  monthday: string;
  weekdayOrdinal: string; // "1" | "2" | "3" | "4" | "-1" (last)
  weekday: string; // "MO".."SU"
  /** YEARLY only. */
  yearMonth: string; // "1".."12"
  yearDay: string; // "1".."31"
  anchor: "schedule" | "completion";
  endMode: "never" | "until" | "count";
  until: string;
  count: string;
};

export const EMPTY_RECURRENCE: RecurrenceFormValues = {
  enabled: false,
  freq: "WEEKLY",
  interval: "1",
  byweekday: [],
  monthMode: "day",
  monthday: "1",
  weekdayOrdinal: "1",
  weekday: "MO",
  yearMonth: "1",
  yearDay: "1",
  anchor: "schedule",
  endMode: "never",
  until: "",
  count: "1",
};

const WEEKDAY_CODES = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"] as const;
const WEEKDAY_NAMES: Record<string, string> = {
  MO: "Monday",
  TU: "Tuesday",
  WE: "Wednesday",
  TH: "Thursday",
  FR: "Friday",
  SA: "Saturday",
  SU: "Sunday",
};
const ORDINAL_WORDS: Record<string, string> = {
  "1": "first",
  "2": "second",
  "3": "third",
  "4": "fourth",
  "-1": "last",
};
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function capitalize(s: string): string {
  return s.length === 0 ? s : s.charAt(0).toUpperCase() + s.slice(1);
}

function plural(n: number, unit: string): string {
  return n === 1 ? unit : `${unit}s`;
}

function intervalPhrase(values: RecurrenceFormValues, unit: string): string {
  const n = Math.max(1, Number(values.interval) || 1);
  if (n === 1) return `every ${unit}`;
  if (n === 2) return `every other ${unit}`; // §4.6's own example: "Every other Saturday"
  return `every ${n} ${plural(n, unit)}`;
}

function endPhrase(values: RecurrenceFormValues): string {
  if (values.endMode === "until" && values.until) return `, until ${values.until}`;
  if (values.endMode === "count" && values.count) {
    const n = Math.max(1, Number(values.count) || 1);
    return `, ${n} ${plural(n, "time")}`;
  }
  return ", forever";
}

/** §4.6.2: "Show the resulting rule in plain English under the controls, never the RRULE string." */
export function describeRecurrence(values: RecurrenceFormValues): string {
  if (!values.enabled) return "Does not repeat.";

  let body: string;
  switch (values.freq) {
    case "DAILY":
      body = capitalize(intervalPhrase(values, "day"));
      break;
    case "WEEKLY":
      body =
        values.byweekday.length === 0
          ? capitalize(intervalPhrase(values, "week"))
          : `${capitalize(intervalPhrase(values, "week"))} on ` +
            values.byweekday.map((d) => WEEKDAY_NAMES[d]).join(", ");
      break;
    case "MONTHLY":
      body =
        values.monthMode === "day"
          ? `${capitalize(intervalPhrase(values, "month"))} on day ${values.monthday || "1"}`
          : `${capitalize(intervalPhrase(values, "month"))} on the ` +
            `${ORDINAL_WORDS[values.weekdayOrdinal] ?? values.weekdayOrdinal} ` +
            `${WEEKDAY_NAMES[values.weekday]}`;
      break;
    case "YEARLY": {
      const month = MONTH_NAMES[Number(values.yearMonth) - 1] ?? MONTH_NAMES[0];
      body = `Every year on ${month} ${values.yearDay || "1"}`;
      break;
    }
  }
  return body + endPhrase(values);
}

/** The bare RRULE EFSS stores — no `DTSTART`, no `RRULE:` prefix (§4.6, §7). */
export function buildRRuleString(values: RecurrenceFormValues): string {
  const parts: string[] = [`FREQ=${values.freq}`];
  const interval = Math.max(1, Number(values.interval) || 1);
  if (interval > 1) parts.push(`INTERVAL=${interval}`);

  if (values.freq === "WEEKLY" && values.byweekday.length > 0) {
    parts.push(`BYDAY=${values.byweekday.join(",")}`);
  }
  if (values.freq === "MONTHLY") {
    if (values.monthMode === "day") {
      parts.push(`BYMONTHDAY=${Number(values.monthday) || 1}`);
    } else {
      parts.push(`BYDAY=${values.weekday}`);
      parts.push(`BYSETPOS=${values.weekdayOrdinal}`);
    }
  }
  if (values.freq === "YEARLY") {
    parts.push(`BYMONTH=${Number(values.yearMonth) || 1}`);
    parts.push(`BYMONTHDAY=${Number(values.yearDay) || 1}`);
  }
  // UNTIL is written without a time or `Z` suffix — dateutil anchors every rule at a naive
  // local date (§5.2), and a UTC-suffixed UNTIL against a naive DTSTART is a parse error.
  if (values.endMode === "until" && values.until) {
    parts.push(`UNTIL=${values.until.replaceAll("-", "")}`);
  } else if (values.endMode === "count" && values.count) {
    parts.push(`COUNT=${Math.max(1, Number(values.count) || 1)}`);
  }
  return parts.join(";");
}

const FREQ_BY_NUMBER: Record<number, Freq> = {
  [RRule.YEARLY]: "YEARLY",
  [RRule.MONTHLY]: "MONTHLY",
  [RRule.WEEKLY]: "WEEKLY",
  [RRule.DAILY]: "DAILY",
};

function weekdayCode(w: Weekday | number | string): string {
  if (typeof w === "number") return WEEKDAY_CODES[w];
  if (typeof w === "string") return w;
  return w.toString();
}

/** Reconstruct form state from a stored `rrule` — used when opening an existing task. */
export function parseRecurrence(
  rrule: string | null,
  recurAnchor: "schedule" | "completion" | null,
): RecurrenceFormValues {
  if (!rrule) return EMPTY_RECURRENCE;
  try {
    const opts = RRule.fromString(rrule).origOptions;
    const freq = FREQ_BY_NUMBER[opts.freq as number];
    if (!freq) return EMPTY_RECURRENCE;

    const byweekdayRaw = opts.byweekday;
    const byweekday = (Array.isArray(byweekdayRaw) ? byweekdayRaw : byweekdayRaw ? [byweekdayRaw] : []).map(
      weekdayCode,
    );
    const bysetpos = Array.isArray(opts.bysetpos) ? opts.bysetpos[0] : opts.bysetpos;
    const hasSetPos = bysetpos !== undefined;

    return {
      ...EMPTY_RECURRENCE,
      enabled: true,
      freq,
      interval: String(opts.interval ?? 1),
      byweekday: freq === "WEEKLY" ? byweekday : [],
      monthMode: hasSetPos ? "weekday" : "day",
      monthday: freq === "MONTHLY" && !hasSetPos ? String(opts.bymonthday ?? 1) : EMPTY_RECURRENCE.monthday,
      weekdayOrdinal: hasSetPos ? String(bysetpos) : EMPTY_RECURRENCE.weekdayOrdinal,
      weekday: hasSetPos && byweekday[0] ? byweekday[0] : EMPTY_RECURRENCE.weekday,
      yearMonth: freq === "YEARLY" ? String(opts.bymonth ?? 1) : EMPTY_RECURRENCE.yearMonth,
      yearDay: freq === "YEARLY" ? String(opts.bymonthday ?? 1) : EMPTY_RECURRENCE.yearDay,
      anchor: recurAnchor ?? "schedule",
      endMode: opts.until ? "until" : opts.count ? "count" : "never",
      until: opts.until ? new Date(opts.until).toISOString().slice(0, 10) : "",
      count: opts.count ? String(opts.count) : "1",
    };
  } catch {
    return EMPTY_RECURRENCE;
  }
}

/** Up to `count` local occurrence dates, for the form's own "next: …" confirmation line. */
export function previewDates(rruleString: string, recurStart: string, count = 3): string[] {
  if (!recurStart) return [];
  try {
    const base = RRule.fromString(rruleString);
    const [y, m, d] = recurStart.split("-").map(Number);
    if (!y || !m || !d) return [];
    const withStart = new RRule({ ...base.origOptions, dtstart: new Date(Date.UTC(y, m - 1, d)) });
    return withStart.all((_, i) => i < count).map((date) => date.toISOString().slice(0, 10));
  } catch {
    return [];
  }
}
